import { api, ApiError } from './client';

/**
 * ADR-010 — the write-back bridge client. These endpoints command the client's
 * real Apskaita5 book (the legacy plane stays the single accounting writer);
 * they are user-action-only and 503 when the Writeback kill switch is off.
 */

export type WritebackPromotionState =
  | 'Confirmed'   // success — legacy confirmed the posting
  | 'Reconciled'  // success — sync brought the entry back and matched it
  | 'Failed'      // rejected by legacy — the user may retry ("Pakartoti")
  | 'Unknown'     // transport outcome unknown — user resolves via "Patikrinti rezultatą"
  | 'Pending'     // command in flight
  | 'Rejected';   // guard failure (wrong workspace kind, not a draft, 409, …)

export interface WritebackPromotionResult {
  state: WritebackPromotionState;
  commandId: string;
  legacyEntryId: number | null;
  errorCode: string | null;
  /** Ready-to-display LT outcome copy from the API — show verbatim. */
  message: string;
  httpStatus: number;
}

/** JournalEntry/Invoice.A5SuiteIdFloor — ids at or above this are a5suite drafts. */
export const A5_SUITE_ID_FLOOR = 1_000_000_000;

/** ADR-012 invoice lane: 'made' (sales) or 'received' (supplier). */
export type WritebackInvoiceKind = 'made' | 'received';

/** Rule: the promote affordance exists only for unposted a5suite drafts. */
export function isPromotableDraft(entry: { id: number; hasBeenPosted?: boolean; isPosted?: boolean }): boolean {
  return entry.id >= A5_SUITE_ID_FLOOR && entry.hasBeenPosted !== true && entry.isPosted !== true;
}

/**
 * Guard/kill-switch failures arrive as non-2xx with the SAME JSON result body
 * (state + message). The shared fetch wrapper throws on !ok — recover the body
 * so the UI shows the API's own Lithuanian outcome copy verbatim (never invent
 * error text client-side). Plain-text bodies (the 503 kill switch) show as-is.
 */
function resultFromError(err: unknown): WritebackPromotionResult {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.body) as Partial<WritebackPromotionResult>;
      if (typeof parsed?.message === 'string' && parsed.message) {
        return {
          state: (parsed.state as WritebackPromotionState) ?? 'Rejected',
          commandId: parsed.commandId ?? '',
          legacyEntryId: parsed.legacyEntryId ?? null,
          errorCode: parsed.errorCode ?? null,
          message: parsed.message,
          httpStatus: err.status,
        };
      }
    } catch {
      // body is not the JSON result shape — fall through to the raw text
    }
    if (err.body) {
      return {
        state: 'Rejected',
        commandId: '',
        legacyEntryId: null,
        errorCode: null,
        message: err.body,
        httpStatus: err.status,
      };
    }
  }
  throw err;
}

export const writebackApi = {
  /** User action: register a draft journal entry in the client's book. */
  promote: async (id: number): Promise<WritebackPromotionResult> => {
    try {
      return await api.post<WritebackPromotionResult>(`/writeback/journal-entries/${id}/promote`);
    } catch (err) {
      return resultFromError(err);
    }
  },

  /** User action: resolve an Unknown outcome by probing the legacy ledger. */
  checkOutcome: async (id: number): Promise<WritebackPromotionResult> => {
    try {
      return await api.post<WritebackPromotionResult>(`/writeback/journal-entries/${id}/promotion/check`);
    } catch (err) {
      return resultFromError(err);
    }
  },

  /** Current promotion status of a draft; null when it was never promoted (404). */
  getPromotion: async (id: number): Promise<WritebackPromotionResult | null> => {
    try {
      return await api.get<WritebackPromotionResult>(`/writeback/journal-entries/${id}/promotion`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  /** ADR-012 user action: issue/register a draft invoice in the client's book. */
  promoteInvoice: async (kind: WritebackInvoiceKind, id: number): Promise<WritebackPromotionResult> => {
    try {
      return await api.post<WritebackPromotionResult>(`/writeback/invoices/${kind}/${id}/promote`);
    } catch (err) {
      return resultFromError(err);
    }
  },

  /**
   * ADR-012 user action: resolve an Unknown invoice outcome by REPLAYING the
   * same command id (the legacy ledger makes it idempotent — there is no GET
   * probe on the invoice lanes; the vanilla GET routes delete).
   */
  checkInvoiceOutcome: async (kind: WritebackInvoiceKind, id: number): Promise<WritebackPromotionResult> => {
    try {
      return await api.post<WritebackPromotionResult>(`/writeback/invoices/${kind}/${id}/promotion/check`);
    } catch (err) {
      return resultFromError(err);
    }
  },

  /** Current invoice promotion status; null when it was never promoted (404). */
  getInvoicePromotion: async (kind: WritebackInvoiceKind, id: number): Promise<WritebackPromotionResult | null> => {
    try {
      return await api.get<WritebackPromotionResult>(`/writeback/invoices/${kind}/${id}/promotion`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
};
